import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AuthenticationResult,
  PopupRequest,
  PublicClientApplication,
  RedirectRequest
} from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppConstants } from '../constants/app.contants';
import { UserService } from './user.service';

const RETURN_URL_KEY = 'msal_return_url';

interface EncryptedMicrosoftAuthConfig {
  cipherText: string;
  iv: string;
  tag: string;
}

interface MicrosoftAuthConfig {
  tenantId: string;
  clientId: string;
}

@Injectable({ providedIn: 'root' })
export class MicrosoftAuthService {
  private msalInstance: PublicClientApplication | null = null;
  private initialized = false;
  private lastErrorMessage: string | null = null;

  constructor(
    private http: HttpClient,
    private userService: UserService
  ) {}

  private async getInstance(): Promise<PublicClientApplication> {
    if (!this.msalInstance) {
      const configResponse = await firstValueFrom(
        this.http.get<any>(`${environment.API_URL}/${environment.APIs_PREFIX}/user/microsoft-auth-config`)
      );

      if (!configResponse?.isSuccess) {
        throw new Error(configResponse?.errorMessageEn || 'Microsoft authentication is not configured.');
      }

      const { tenantId, clientId } = await this.decryptConfiguration(configResponse.data);
      this.msalInstance = new PublicClientApplication({
        auth: {
          clientId,
          authority: `https://login.microsoftonline.com/${tenantId}`,
          redirectUri: `${window.location.origin}/${AppConstants.ROUTES.AUTHENTICATION}/${AppConstants.ROUTES.MICROSOFT_CALLBACK}`
        },
        cache: { cacheLocation: 'localStorage' }
      });
    }

    if (!this.initialized) {
      await this.msalInstance.initialize();
      this.initialized = true;
    }

    return this.msalInstance;
  }

  private async decryptConfiguration(encryptedConfig: EncryptedMicrosoftAuthConfig): Promise<MicrosoftAuthConfig> {
    const encryptionKey = environment.MICROSOFT_AUTH_CONFIG_KEY;
    if (!encryptionKey || encryptionKey === 'YOUR_BASE64_32_BYTE_KEY_HERE') {
      throw new Error('Microsoft authentication encryption key is not configured.');
    }

    const keyBytes = this.fromBase64(encryptionKey);
    if (keyBytes.length !== 32) {
      throw new Error('Microsoft authentication encryption key must be 32 bytes.');
    }

    const cipherText = this.fromBase64(encryptedConfig?.cipherText);
    const iv = this.fromBase64(encryptedConfig?.iv);
    const tag = this.fromBase64(encryptedConfig?.tag);
    if (!cipherText.length || iv.length !== 12 || tag.length !== 16) {
      throw new Error('Microsoft authentication configuration is invalid.');
    }

    const payload = new Uint8Array(cipherText.length + tag.length);
    payload.set(cipherText);
    payload.set(tag, cipherText.length);

    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );
    const decrypted = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      cryptoKey,
      payload
    );

    return JSON.parse(new TextDecoder().decode(decrypted)) as MicrosoftAuthConfig;
  }

  private fromBase64(value: string | undefined): Uint8Array {
    if (!value) {
      return new Uint8Array();
    }

    const binary = window.atob(value);
    return Uint8Array.from(binary, character => character.charCodeAt(0));
  }

  private async completeLogin(idToken: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.post<any>(
        `${environment.API_URL}/${environment.APIs_PREFIX}/user/microsoft-login`,
        { idToken }
      )
    );

    if (response?.isSuccess) {
      this.lastErrorMessage = null;
      this.userService.handleValidAuthentication(response);
      return true;
    }

    this.lastErrorMessage = response?.errorMessageEn || 'Microsoft login was rejected.';
    return false;
  }

  getLastErrorMessage(): string | null {
    return this.lastErrorMessage;
  }

  async loginWithPopup(): Promise<boolean> {
    try {
      const instance = await this.getInstance();
      const request: PopupRequest = {
        scopes: ['openid', 'profile', 'email'],
        prompt: 'select_account'
      };
      const result = await instance.loginPopup(request);
      return result?.idToken ? this.completeLogin(result.idToken) : false;
    } catch (error) {
      console.error('Microsoft login error:', error);
      return false;
    }
  }

  async loginWithRedirect(returnUrl?: string): Promise<void> {
    const instance = await this.getInstance();
    if (returnUrl) {
      sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    }

    const request: RedirectRequest = {
      scopes: ['openid', 'profile', 'email'],
      prompt: 'select_account'
    };

    try {
      await instance.loginRedirect(request);
    } catch (error: any) {
      if (error?.errorCode === 'interaction_in_progress') {
        Object.keys(sessionStorage)
          .filter(key => key.toLowerCase().includes('interaction'))
          .forEach(key => sessionStorage.removeItem(key));
        await instance.loginRedirect(request);
        return;
      }
      throw error;
    }
  }

  consumeReturnUrl(): string | null {
    const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    return returnUrl;
  }

  async handleRedirectCallback(): Promise<'success' | 'rejected' | 'none'> {
    let instance: PublicClientApplication;
    try {
      instance = await this.getInstance();
    } catch (error) {
      console.error('Microsoft authentication configuration error:', error);
      return 'none';
    }

    let result: AuthenticationResult | null;
    try {
      result = await instance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    } catch (error) {
      console.error('Microsoft redirect processing error:', error);
      this.lastErrorMessage = 'Microsoft sign-in did not complete. Please try again.';
      return 'rejected';
    }

    if (!result?.idToken) {
      return 'none';
    }

    return await this.completeLogin(result.idToken) ? 'success' : 'rejected';
  }

  clearCache(): void {
    Object.keys(localStorage)
      .filter(key => key.toLowerCase().includes('msal'))
      .forEach(key => localStorage.removeItem(key));
    Object.keys(sessionStorage)
      .filter(key => key.toLowerCase().includes('msal') || key === RETURN_URL_KEY)
      .forEach(key => sessionStorage.removeItem(key));

    this.msalInstance = null;
    this.initialized = false;
    this.lastErrorMessage = null;
  }
}
